import { Link } from "react-router-dom";
import useDepartmentDoctors from "../hooks/useDepartmentDoctors";
import DoctorCard from "./DoctorCard";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Phone, Mail, MapPin, CheckCircle2, Users, Award, Clock, Heart, ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import SEO from "../pages/SEO"
import { useAppointment } from "../context/AppointmentContext"
export default function ObstetricsGynaecologyPage() {
  const { open: openAppointment } = useAppointment();
  const doctors = useDepartmentDoctors("Gynaecology");


  const subspecialties = [
    {
      title: "Obstetrics & Maternity Care",
      description: "Antenatal checkups, pregnancy monitoring, painless delivery & C-sections.",
      image: "/Obstetrics&MaternityCare.jpg",
    },
    {
      title: "High-Risk Pregnancy Care",
      description: "Specialized management for gestational diabetes, hypertension, twins & complications.",
      image: "/High-RiskPregnancyCare.jpg",
    },
    {
      title: "Infertility & Reproductive Medicine",
      description: "Evaluation & treatment for infertility, ovulation issues & hormonal imbalance.",
      image: "/Infertility&ReproductiveMedicine.jpg",
    },
    {
      title: "Gynecologic Laparoscopy",
      description: "Minimally invasive surgeries for fibroids, cysts & endometriosis.",
      image: "/GynecologicLaparoscopy.jpg",
    },
    {
      title: "Uro-Gynaecology",
      description: "Treatment for urinary incontinence & pelvic floor disorders.",
      image: "/Uro-Gynaecology.jpg",
    },
    {
      title: "Adolescent & Menopause Care",
      description: "PCOS, puberty issues & menopause support.",
      image: "/Adolescent&MenopauseCare.jpg",
    },
  ];

  const treatments = [
    "Normal & Cesarean Deliveries",
    "High-Risk Pregnancy & Fetal Monitoring",
    "Laparoscopic Hysterectomy",
    "Fibroid, Polyp & Ovarian Cyst Removal",
    "Endometriosis Treatment",
    "Fertility Evaluation & Ovulation Induction",
    "Colposcopy & Pap Smear Screening",
    "Menstrual Disorder Management (PCOS, PMS, Irregular Periods)",
    "Menopause Counseling & Hormone Therapy",
  ];

  const whyChoose = [
    { icon: Users, title: "Experienced Specialists", description: "Experienced obstetricians & gynecologic surgeons" },
    { icon: Clock, title: "24x7 Maternity Care", description: "24x7 maternity & emergency obstetric care" },
    { icon: Heart, title: "Advanced Monitoring", description: "Advanced fetal monitoring & neonatal support" },
    { icon: Award, title: "Minimally Invasive", description: "Minimally invasive gynecology & laparoscopy" },
    { icon: CheckCircle2, title: "Personalized Programs", description: "Personalized fertility and hormonal treatment programs" },
  ];

  const articles = [
    {
      title: "10 Essential Tips for a Healthy Pregnancy",
      excerpt: "Learn the most important steps to ensure a healthy pregnancy journey from nutrition to prenatal care.",
    },
    {
      title: "Understanding PCOS and Hormonal Imbalance",
      excerpt: "Comprehensive guide to polycystic ovary syndrome, symptoms, diagnosis, and treatment options.",
    },
    {
      title: "When Should You Visit a Gynecologist?",
      excerpt: "Important signs and symptoms that indicate it's time to schedule a gynecological consultation.",
    },
    {
      title: "How to Prepare for Childbirth — A Complete Guide",
      excerpt: "Everything you need to know about preparing physically and mentally for childbirth and delivery.",
    },
  ];

  const faqs = [
    {
      question: "Do you handle high-risk pregnancies?",
      answer:
        "Yes, our specialists are trained to manage all types of high-risk obstetric conditions including gestational diabetes, hypertension, multiple pregnancies, and other complications. We provide comprehensive monitoring and specialized care throughout the pregnancy.",
    },
    {
      question: "Is laparoscopic surgery available for gynecology?",
      answer:
        "Yes, we perform advanced minimally invasive surgeries for fibroids, ovarian cysts, endometriosis, and hysterectomy. Laparoscopic procedures offer faster recovery, less pain, and minimal scarring compared to traditional open surgery.",
    },
    {
      question: "Do you offer fertility treatments?",
      answer:
        "Yes, we provide complete infertility evaluation and treatment including hormonal therapy, ovulation induction, and reproductive health counseling. Our specialists work closely with each couple to develop personalized treatment plans.",
    },
  ];
  const schema = [
  {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    "name": "MAMS Hospitals Obstetrics & Gynaecology Department",
    "url": "https://mamshospitals.com/obstetrics-gynaecology",
    "logo": "https://mamshospitals.com/MAMS-Logo.png",
    "image": "https://mamshospitals.com/Obstetrics&Gynaecology.jpg",
    "description": "MAMS Hospitals provides advanced obstetrics and gynaecology treatment in Hyderabad including maternity care, high-risk pregnancy care, infertility treatment, laparoscopic gynecology, PCOS management, and menopause care.",
    "medicalSpecialty": "Obstetrics and Gynecology",
    "telephone": "+91-7399999981",
    "priceRange": "$$",

    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Bachupally",
      "addressLocality": "Hyderabad",
      "addressRegion": "Telangana",
      "postalCode": "500090",
      "addressCountry": "IN"
    },

    "availableService": [
      {
        "@type": "MedicalProcedure",
        "name": "Normal & Cesarean Deliveries"
      },
      {
        "@type": "MedicalProcedure",
        "name": "High-Risk Pregnancy Care"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Infertility Treatment"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Laparoscopic Gynecology Surgery"
      },
      {
        "@type": "MedicalProcedure",
        "name": "PCOS & Hormonal Disorder Treatment"
      }
    ],

    "sameAs": [
      "https://www.facebook.com/MAMSHospitals",
      "https://www.instagram.com/mams_hospitals"
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Do you handle high-risk pregnancies?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, MAMS Hospitals specializes in high-risk pregnancy care including gestational diabetes, hypertension, twins, and pregnancy complications."
        }
      },
      {
        "@type": "Question",
        "name": "Is laparoscopic surgery available for gynecology?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, MAMS Hospitals performs advanced minimally invasive laparoscopic gynecology procedures for fibroids, cysts, and endometriosis."
        }
      },
      {
        "@type": "Question",
        "name": "Do you provide fertility treatments?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, MAMS Hospitals offers infertility evaluation, ovulation induction, hormonal treatment, and fertility counseling."
        }
      }
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Obstetrics & Gynaecology Department - MAMS Hospitals",
    "url": "https://mamshospitals.com/obstetrics-gynaecology",
    "description": "Best obstetrics and gynecology hospital in Hyderabad offering maternity care, infertility treatment, laparoscopic gynecology surgery, PCOS management, and high-risk pregnancy care.",
    "keywords": [
      "Best Gynecologist Hyderabad",
      "Maternity Hospital Hyderabad",
      "Pregnancy Care Hyderabad",
      "Infertility Treatment Hyderabad",
      "PCOS Treatment Hyderabad",
      "Laparoscopic Gynecology Hyderabad",
      "Gynecology Hospital Bachupally"
    ]
  }
];

  return (
    <>
    <SEO
      title="Best Gynecology & Maternity Hospital in Hyderabad | Pregnancy Care | MAMS Hospitals"
      description="MAMS Hospitals offers advanced obstetrics and gynecology treatment in Hyderabad including maternity care, infertility treatment, PCOS management, high-risk pregnancy care, and laparoscopic gynecology surgery."
      keywords="best gynecologist Hyderabad,
    maternity hospital Hyderabad,
    pregnancy care Hyderabad,
    high-risk pregnancy Hyderabad,
    infertility treatment Hyderabad,
    PCOS treatment Hyderabad,
    normal delivery Hyderabad,
    cesarean delivery Hyderabad,
    laparoscopic gynecology Hyderabad,
    women health specialist Hyderabad,
    fertility treatment Hyderabad,
    gynecology hospital Bachupally,
    MAMS Hospitals Gynecology"
      schema={schema}
    />
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-50 via-white to-blue-50 py-12 md:py-24">
        <div className="container mx-auto px-0 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                Best Obstetrics & Gynaecology Hospital in Hyderabad
              </div>

              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
                Compassionate Care for Every Stage of Womanhood
              </h1>

              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                MAMS Hospitals offers comprehensive women's healthcare covering pregnancy, reproductive health, hormonal
                care, fertility, and advanced gynecologic surgeries with experienced specialists.
              </p>

             <div className="flex flex-col sm:flex-row gap-4">

                <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8" size="lg" onClick={openAppointment}>
                    Book Appointment
                  </Button>

                <Link to="tel:+91-7399999981">
                  <Button
                    className="border-teal-600 text-teal-600 hover:bg-teal-50 rounded-full px-8"
                    variant="outline"
                    size="lg"
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    Call Us
                  </Button>
                </Link>

              </div>
            </div>

            <div className="relative">
              <div className="absolute -top-4 -right-4 bg-teal-600 text-white p-6 rounded-2xl shadow-xl z-10">
                <div className="text-3xl font-bold">24/7</div>
                <div className="text-sm">Maternity Care</div>
              </div>

              <img
                src="/Obstetrics&Gynaecology.jpg"
                alt="Obstetrics & Gynaecology at MAMS Hospital"
                className="rounded-2xl shadow-2xl w-full"
              canonicalUrl="https://mamshospitals.com/departments/obstetrics-gynaecology"
      />
            </div>
          </div>
        </div>
      </section>

      {/* Sub-Specialties */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-0 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Sub-Specialties in Obstetrics & Gynaecology
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Comprehensive women's healthcare from adolescence to motherhood and beyond
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {subspecialties.map((specialty, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-gray-200 hover:border-teal-500">
                <CardContent className="p-0">
                  <div className="relative h-48 overflow-hidden rounded-t-lg">
                    <img
                      src={specialty.image}
                      alt={specialty.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>

                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{specialty.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{specialty.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Treatments & Procedures */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-0 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Treatments & Procedures</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Comprehensive obstetric and gynecologic care with advanced treatments
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {treatments.map((treatment, index) => (
              <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm">
                <CheckCircle2 className="h-6 w-6 text-teal-600 mt-1" />
                <span className="text-gray-700 font-medium">{treatment}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-0 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose MAMS Hospitals for Obstetrics & Gynaecology
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">Trusted women's healthcare with compassionate specialists</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChoose.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="border-gray-200 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                      <Icon className="h-7 w-7 text-teal-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600">Convenient location near Miyapur & Bachupally</p>
          </div>
        </div>
      </section>

      {/* Specialist */}
                      {doctors.length > 0 && (
          <section className="py-16 px-0 bg-apple-gray-50">
            <div className="container mx-auto max-w-6xl">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-semibold text-apple-gray-500 mb-4">Meet Our Specialists</h2>
              </div>
              <div className="relative">
                <button
                  onClick={() => { const el = document.getElementById('doc-scroll'); if(el) el.scrollBy({ left: -340, behavior: 'smooth' }); }}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-gray-100"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
                <div id="doc-scroll" className="flex gap-6 overflow-x-auto scroll-smooth px-10 scrollbar-hide pb-4">
                  {doctors.map(doctor => (
                    <div key={doctor.id} className="w-[280px] sm:w-[300px] flex-shrink-0">
                      <DoctorCard doctor={{
                        name: doctor.title.rendered,
                        slug: doctor.slug,
                        imageUrl: doctor._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "",
                        specialty: doctor._embedded?.["wp:term"]?.[0]?.[0]?.name || "",
                        designation: doctor.acf?.designation || "",
                        qualification: doctor.acf?.qualification || "",
                        experience: doctor.acf?.experience || "",
                      }} />
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => { const el = document.getElementById('doc-scroll'); if(el) el.scrollBy({ left: 340, behavior: 'smooth' }); }}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-gray-100"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
              <div className="text-center mt-8">
                <Link to="/doctors">
                  <button className="px-6 py-3 rounded-full border text-sm font-medium inline-flex items-center gap-2">
                    View All Doctors
                  </button>
                </Link>
              </div>
            </div>
          </section>
          )}

      {/* Articles */}
      {/* <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-0 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Recent Articles</h2>
            <p className="text-lg text-gray-600">Stay informed about women's health and pregnancy care</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {articles.map((article, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-gray-200 cursor-pointer">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-teal-600">
                    {article.title}
                  </h3>
                  <p className="text-gray-600">{article.excerpt}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section> */}

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-0 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">Get answers to common questions about women's healthcare</p>
          </div>

          <Accordion type="single" collapsible className="bg-white rounded-lg shadow-sm">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="px-6 text-left hover:text-teal-600">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="px-6 text-gray-600">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Contact & Appointment Section */}
      <section className="py-16 px-0 bg-apple-teal text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">Book an Appointment</h2>
            <p className="text-white/90 max-w-2xl mx-auto">
              Your heart health is our top priority. Book an appointment today with Hyderabad's trusted experts at MAMS Hospitals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Phone className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Call Us</h3>
                <a href="tel:+917399999981" className="text-white/90 hover:text-white">
                  +91-7399999981
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Mail className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Email Us</h3>
                <a href="mailto: info@mamshospitals.com" className="text-white/90 hover:text-white">
                   info@mamshospitals.com
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <MapPin className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Visit Us</h3>
                <p className="text-white/90">Bachupally, Miyapur</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            {/* <Link href="/book-appointment">
              <Button size="lg" variant="secondary" className="rounded-full">
                <Calendar className="mr-2 h-5 w-5" />
                Schedule Your Appointment
              </Button>
            </Link> */}
          </div>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="py-8 px-0 bg-red-600 text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Clock className="h-8 w-8" />
              <div>
                <h3 className="text-xl font-bold">24/7 Emergency Care</h3>
                <p className="text-white/90">Fully equipped emergency department</p>
              </div>
            </div>
            <a href="tel:+917399999981">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto rounded-full bg-white text-red-600 hover:bg-gray-100">
                <Phone className="mr-2 h-5 w-5" />
                Call Emergency
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}
